/**
 * Request-a-new-link landing page (Phase 4 Action 45).
 *
 * URL: /auth/magic/request-new
 *
 * Customer arrives here from the MagicLinkLanding error states
 * (expired / already_consumed / not_found). Form collects email and
 * posts to /admin/auth/magic-link/request-new. The backend writes a
 * row to link_requests and emails the PowerMove ops address.
 *
 * Surfaces the 3/hour rate limit prominently — both in the form
 * caption ("up to 3/hour") and in the error state when exceeded
 * ("try again in ~N minutes").
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { API_ORIGIN } from '../api/baseUrl';

const MAX_PER_HOUR = 3;

interface ApiSuccess {
  data: { request_id: string; message: string };
}

interface ApiError {
  error: string;
  message: string;
  rateLimit?: { maxPerHour: number; retryAfterMs: number };
}

type ApiResult = ApiSuccess | ApiError;

function isError(r: ApiResult): r is ApiError {
  return 'error' in r;
}

export default function RequestNewLink() {
  const [params] = useSearchParams();
  // Optional ?email= prefill from the upstream landing-page button
  // (not wired today but reserved so a future change to the error
  // page can pass it forward).
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [state, setState] = useState<
    | { kind: 'idle' }
    | { kind: 'success'; message: string }
    | { kind: 'rate_limited'; retryAfterMin: number }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setState({ kind: 'idle' });
    try {
      const res = await fetch(`${API_ORIGIN}/api/admin/auth/magic-link/request-new`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const body = (await res.json()) as ApiResult;
      if (!res.ok || isError(body)) {
        const err = body as ApiError;
        if (err.error === 'rate_limited' && err.rateLimit) {
          const min = Math.ceil(err.rateLimit.retryAfterMs / 60_000);
          setState({ kind: 'rate_limited', retryAfterMin: min });
        } else {
          setState({ kind: 'error', message: err.message ?? 'Something went wrong.' });
        }
        return;
      }
      setState({ kind: 'success', message: body.data.message });
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Network error',
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md rounded-md border border-slate-200 bg-white p-8 shadow-sm">
        <h1 className="text-xl font-semibold text-slate-900">Request a new magic link</h1>
        <p className="mt-2 text-sm text-slate-600">
          Enter your email and we'll alert the PowerMove team to send you a fresh invite.
          You can request up to {MAX_PER_HOUR} new links per hour from the same email.
        </p>

        {state.kind === 'success' && (
          <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
            {state.message}
          </div>
        )}

        {state.kind === 'rate_limited' && (
          <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            You've reached the limit of {MAX_PER_HOUR} requests per hour from this email.
            Please try again in about {state.retryAfterMin} minute{state.retryAfterMin === 1 ? '' : 's'}.
          </div>
        )}

        {state.kind === 'error' && (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {state.message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-5 space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-700">
              Email
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting || state.kind === 'success'}
              placeholder="you@yourcompany.com"
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-600 focus:outline-none focus:ring-1 focus:ring-slate-600 disabled:bg-slate-50"
            />
          </div>
          <button
            type="submit"
            disabled={submitting || state.kind === 'success' || !email.trim()}
            className="inline-flex w-full justify-center rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
          >
            {submitting ? 'Sending…' : state.kind === 'success' ? 'Request sent' : 'Request a new link'}
          </button>
        </form>

        <p className="mt-4 text-center text-xs text-slate-500">
          Limit: {MAX_PER_HOUR} requests per email per rolling hour.
        </p>
      </div>
    </div>
  );
}
